/**
 * Auth Redux slice
 */
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { authAPI } from '@/api/authAPI';
import { User, LoginCredentials, RegisterData } from '@/types';
import { TOKEN_KEY, REFRESH_TOKEN_KEY, USER_KEY } from '@/utils/constants';
import { PARENT_APP_ALLOWED_ORIGINS } from '@/utils/parentAppOrigins';
import toast from 'react-hot-toast';

const AUTH_MODE_KEY = 'jira_clone_auth_mode';

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isAdmin: boolean;  // Track if user is admin
  mode: string | null;
  loading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  token: localStorage.getItem(TOKEN_KEY),
  isAuthenticated: false, // Will be set to true after user data is loaded
  isAdmin: false,  // Will be set after checking user role
  mode: sessionStorage.getItem(AUTH_MODE_KEY),
  loading: true, // block until initializeAuth resolves
  error: null,
};

// Helper function to add computed name field
export const addNameField = (user: User): User => {
  return {
    ...user,
    name: user.display_name || `${user.first_name} ${user.last_name}`.trim()
  };
};

// Async thunks
export const login = createAsyncThunk(
  'auth/login',
  async (credentials: LoginCredentials, { rejectWithValue }) => {
    try {
      const tokenResponse = await authAPI.login(credentials);
      localStorage.setItem(TOKEN_KEY, tokenResponse.access_token);
      localStorage.setItem(REFRESH_TOKEN_KEY, tokenResponse.refresh_token);
      
      // The tokenResponse already contains the user data
      const user = addNameField(tokenResponse.user);
      try { localStorage.setItem(USER_KEY, JSON.stringify(user)); } catch {}
      sessionStorage.removeItem(AUTH_MODE_KEY);
      toast.success('Welcome back!');
      return { user, token: tokenResponse.access_token };
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Login failed';
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const register = createAsyncThunk(
  'auth/register',
  async (data: RegisterData, { rejectWithValue }) => {
    try {
      await authAPI.register(data);
      toast.success('Registration successful! Please login.');
      return null;
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Registration failed');
      return rejectWithValue(error.response?.data?.detail || 'Registration failed');
    }
  }
);

export const getCurrentUser = createAsyncThunk(
  'auth/getCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const user = addNameField(await authAPI.getCurrentUser());
      return user;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to get user');
    }
  }
);

export const updateCurrentUser = createAsyncThunk(
  'auth/updateCurrentUser',
  async (updateData: any, { rejectWithValue }) => {
    try {
      const { userAPI } = await import('@/api/userAPI');
      const user = addNameField(await userAPI.updateCurrentUser(updateData));
      return user;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to update user');
    }
  }
);

export const checkIsAdmin = createAsyncThunk(
  'auth/checkIsAdmin',
  async (_, { rejectWithValue }) => {
    try {
      const { userAPI } = await import('@/api/userAPI');
      const isAdmin = await userAPI.isCurrentUserAdmin();
      return isAdmin;
    } catch (error: any) {
      return rejectWithValue(false);
    }
  }
);

export const logout = createAsyncThunk('auth/logout', async () => {
  authAPI.logout();
  sessionStorage.removeItem(AUTH_MODE_KEY);
  if (window.parent && window.parent !== window) {
    const allowedOrigins = PARENT_APP_ALLOWED_ORIGINS;

    const payload = {
      type: 'session-logged-out',
      source: 'project-tracker-ui',
      at: new Date().toISOString(),
    };

    if (allowedOrigins.length > 0) {
      allowedOrigins.forEach((origin: string) => {
        window.parent.postMessage(payload, origin);
      });
    } else {
      window.parent.postMessage(payload, '*');
    }
  }
  toast.success('Logged out successfully');
});

export const initializeAuth = createAsyncThunk(
  'auth/initialize',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      if (token) {
        // Optionally prime from cached user to avoid UI flicker
        const cachedRaw = localStorage.getItem(USER_KEY);
        if (cachedRaw) {
          try {
            const cached = addNameField(JSON.parse(cachedRaw));
            return { user: cached, token };
          } catch {}
        }
        try {
          const user = addNameField(await authAPI.getCurrentUser());
          try { localStorage.setItem(USER_KEY, JSON.stringify(user)); } catch {}
          
          // Clear any old cached numeric project IDs (migration cleanup)
          const keysToCheck = Object.keys(localStorage);
          keysToCheck.forEach(key => {
            if (key.includes('project') || key.includes('redux')) {
              const value = localStorage.getItem(key);
              if (value && (value.includes('"id":1') || value.includes('"id":2') || value.includes('"id":3') || value.includes('"id":532'))) {
                localStorage.removeItem(key);
              }
            }
          });
          
          return { user, token };
        } catch (err) {
          // If /auth/me fails, try a single refresh then retry /auth/me
          const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
          if (refreshToken) {
            try {
              const refreshed = await authAPI.refreshToken(refreshToken);
              localStorage.setItem(TOKEN_KEY, refreshed.access_token);
              localStorage.setItem(REFRESH_TOKEN_KEY, refreshed.refresh_token);
              const user = addNameField(await authAPI.getCurrentUser());
              try { localStorage.setItem(USER_KEY, JSON.stringify(user)); } catch {}
              return { user, token: refreshed.access_token };
            } catch (refreshErr) {
              // fallthrough to invalid token below
            }
          }
          // No refresh or failed
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(REFRESH_TOKEN_KEY);
          localStorage.removeItem(USER_KEY);
          return rejectWithValue('Invalid token');
        }
      }
      return null;
    } catch (error: any) {
      // Token is invalid, clear it
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      return rejectWithValue('Invalid token');
    }
  }
);

// Slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setAuthCredentials: (state, action: PayloadAction<{ user: User; token: string; mode?: string | null }>) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.isAuthenticated = true;
      state.mode = action.payload.mode === 'embed' ? 'embed' : null;
      if (state.mode === 'embed') {
        sessionStorage.setItem(AUTH_MODE_KEY, 'embed');
      } else {
        sessionStorage.removeItem(AUTH_MODE_KEY);
      }
    },
  },
  extraReducers: (builder) => {
    // Login
    builder.addCase(login.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(login.fulfilled, (state, action) => {
      state.loading = false;
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.isAuthenticated = true;
      state.mode = null;
      // Note: isAdmin will be checked separately via checkIsAdmin thunk (dispatched in App.tsx)
    });
    builder.addCase(login.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Register
    builder.addCase(register.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(register.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(register.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Get current user
    builder.addCase(getCurrentUser.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getCurrentUser.fulfilled, (state, action) => {
      state.loading = false;
      state.user = action.payload;
      state.isAuthenticated = true;
    });
    builder.addCase(getCurrentUser.rejected, (state) => {
      // Do not clear token here; let initializeAuth decide
      state.loading = false;
    });

    // Logout
    builder.addCase(logout.fulfilled, (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.isAdmin = false;
      state.mode = null;
    });

    // Initialize auth
    builder.addCase(initializeAuth.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(initializeAuth.fulfilled, (state, action) => {
      if (action.payload) {
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
      }
      state.loading = false;
    });
    builder.addCase(initializeAuth.rejected, (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.mode = null;
      state.loading = false;
    });

    // Check if user is admin
    builder.addCase(checkIsAdmin.fulfilled, (state, action) => {
      state.isAdmin = action.payload;
    });
    builder.addCase(checkIsAdmin.rejected, (state) => {
      state.isAdmin = false;
    });

    // Update current user
    builder.addCase(updateCurrentUser.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateCurrentUser.fulfilled, (state, action) => {
      state.loading = false;
      state.user = action.payload;
      // Update localStorage
      try { 
        localStorage.setItem(USER_KEY, JSON.stringify(action.payload)); 
      } catch {}
    });
    builder.addCase(updateCurrentUser.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
  },
});

export const { clearError, setAuthCredentials } = authSlice.actions;
export default authSlice.reducer;

